import { ChangeEvent, FC, useState } from 'react'
import { styled } from '@sans-souci/styles'
import { useForm } from 'local-react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { ClientError } from 'graphql-request'

import { createCustomerClient } from '../customerService'
import { useCustomerContext } from '../customerContext'

import {
  validateRequired,
  useFormSubmitState,
  SubmitButton,
  FormTextField,
  FormCheckboxField,
  FormSelectField,
  FormResponseMessage,
} from '@sans-souci/forms'
import { removeEmptyProperties } from '../helpers/removeEmptyProperties'
import { Button } from '@sans-souci/components'
import { formatResponseErrors } from '../helpers/formatResponseErrors'
import { shopifyCountries, UNITED_STATES } from '../helpers/shopifyCountries'
import { Form } from '../components/Form'
import { ShopifyCustomerUserErrorsFieldsFragment } from '@sans-souci/shopify-sdk'

interface RegisterFormProps {
  className?: string
  onCancelClick?: () => void
  onSubmitSuccess?: () => void
}

type FormValues = {
  lastName?: string
  firstName?: string
  address1?: string
  address2?: string
  province?: string
  country?: string
  phone?: string
  zip?: string
  city?: string
  isDefault?: boolean
}

const defaultValues: FormValues = {
  lastName: '',
  firstName: '',
  address1: '',
  address2: '',
  province: '',
  country: UNITED_STATES,
  phone: '',
  zip: '',
  city: '',
  isDefault: false,
}

const HorizontalFieldsContainer = styled('div', {
  display: 'grid',
  gridAutoFlow: 'row',
  gap: '$32',
  '@lg': {
    gap: '$16',
    gridAutoFlow: 'column',
  },
})

export const AddAddressForm: FC<RegisterFormProps> = ({
  className,
  onCancelClick,
  onSubmitSuccess,
}) => {
  const { t } = useTranslation('forms')
  const form = useForm<FormValues>({
    defaultValues,
  })
  const [selectedCountryValue, setSelectedCountryValue] =
    useState(UNITED_STATES)

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    clearErrors,
  } = form

  const { locale } = useRouter()

  const customerClient = createCustomerClient({ locale })

  const {
    invalid,
    success,
    setSuccess,
    setError,
    setFieldError,
    submitMessage,
  } = useFormSubmitState<FormValues>({
    form,
    defaultValues,
    keepValuesOnSuccess: true,
  })
  const { accessToken } = useCustomerContext()
  const customerAccessToken = accessToken || ''

  const handleResponseErrors = (
    errors: ShopifyCustomerUserErrorsFieldsFragment[],
  ) => {
    const { fieldErrors, generalError } =
      formatResponseErrors<FormValues>(errors)
    fieldErrors.forEach((e) => setFieldError(e.fieldName, e.message))
    return setError(generalError)
  }

  const onSubmit = async (data: FormValues) => {
    try {
      clearErrors()
      const { isDefault, ...restOfAddress } = data

      const response = await customerClient.createAddress({
        address: removeEmptyProperties({ ...restOfAddress }),
        customerAccessToken,
      })

      const errors = response?.customerAddressCreate?.customerUserErrors || []

      if (errors?.length) {
        return handleResponseErrors(errors)
      }

      if (!response?.customerAddressCreate?.customerAddress) {
        return setError()
      }

      // set address as default if requested
      if (isDefault) {
        const addressId = response?.customerAddressCreate?.customerAddress?.id

        const defaultAddressResponse =
          await customerClient.updateDefaultAddress({
            addressId,
            customerAccessToken,
          })

        const defaultFieldErrors =
          defaultAddressResponse?.customerDefaultAddressUpdate
            ?.customerUserErrors || []

        if (defaultFieldErrors.length) {
          return handleResponseErrors(defaultFieldErrors)
        }
      }

      setSuccess()
      return onSubmitSuccess && onSubmitSuccess()
    } catch (e: unknown) {
      if (e instanceof ClientError) {
        return setError(e.response.errors?.[0].message)
      }
      return setError()
    }
  }

  const handleSelectedCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountryValue(e.target.value)
  }

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      className={className}
      name="add-address-form"
    >
      <FormSelectField
        control={control}
        rules={validateRequired(t)}
        autoComplete="country"
        name="country"
        value={selectedCountryValue}
        onChange={handleSelectedCountryChange}
      >
        {shopifyCountries.map((countryName) => (
          <option key={countryName} value={countryName}>
            {countryName}
          </option>
        ))}
      </FormSelectField>
      <HorizontalFieldsContainer>
        <FormTextField
          control={control}
          rules={validateRequired(t)}
          autoComplete="name"
          name="firstName"
          label={t('addAddressForm.firstNameFieldLabel')}
        />
        <FormTextField
          control={control}
          rules={validateRequired(t)}
          autoComplete="family-name"
          name="lastName"
          label={t('addAddressForm.lastNameFieldLabel')}
        />
      </HorizontalFieldsContainer>
      <FormTextField
        control={control}
        rules={validateRequired(t)}
        autoComplete="address-line1"
        name="address1"
        label={t('addAddressForm.address1FieldLabel')}
      />
      <FormTextField
        control={control}
        autoComplete="address-line2"
        name="address2"
        label={t('addAddressForm.address2FieldLabel')}
      />
      <HorizontalFieldsContainer>
        <FormTextField
          control={control}
          rules={validateRequired(t)}
          autoComplete="city"
          name="city"
          label={t('addAddressForm.cityFieldLabel')}
        />
        {selectedCountryValue === UNITED_STATES && (
          <FormTextField
            control={control}
            rules={validateRequired(t)}
            autoComplete="address-level1"
            name="province"
            label={t('addAddressForm.provinceFieldLabel')}
          />
        )}
        <FormTextField
          control={control}
          rules={validateRequired(t)}
          autoComplete="postal-code"
          name="zip"
          label={t('addAddressForm.zipFieldLabel')}
        />
      </HorizontalFieldsContainer>
      <FormTextField
        control={control}
        autoComplete="phone"
        name="phone"
        label={t('addAddressForm.phoneFieldLabel')}
      />
      <FormCheckboxField
        control={control}
        autoComplete="isDefault"
        name="isDefault"
        label={t('addAddressForm.setDefaultAddressFieldLabel')}
      />
      <SubmitButton
        text={t('addAddressForm.submitButtonLabel')}
        helpText={submitMessage}
        invalid={invalid}
        success={success}
        loading={isSubmitting}
      />
      {onCancelClick && (
        <Button appearance={'ButtonOutline'} onClick={onCancelClick}>
          Cancel
        </Button>
      )}
      <FormResponseMessage invalid={invalid} message={submitMessage} />
    </Form>
  )
}
